import * as React from "react";
import { AlertCircle, ShieldCheck } from "lucide-react";
import { Button, Dialog } from "@/components/ui";
import { SocoAiLogo } from "@/components/Holo";
import type { HelpDialogProps } from "./PromptStudioHelpDialog";

export const HOW_BACKGROUNDS_WORK_INTRO =
  "Backgrounds are the one part of a design each player can make their own. Pick options from the background library, upload your own, or create new ones with AI, set the default, and decide what players may choose from. The layout, text, colors, and logo stay exactly as you approved them; only the picture behind them changes, and no AI is involved when a player picks one.";

const EXPLAINERS = [
  { title: "Default", body: "What every player sees unless they change it." },
  { title: "Options", body: "The set players can choose from." },
  {
    title: "Library",
    body: "Every background you upload or create joins your library, so you can reuse it on any design. SocialCoach adds a shared set too.",
  },
  {
    title: "Player choices",
    body: '"Suggested only": players choose from the backgrounds on this design. "Any library background": players can also search your background library. "Library or their own photo": players can also upload a photo, which sits behind your layout.',
  },
  { title: "Re-rendering", body: "Changing a background rebuilds the image in a few seconds. Nothing else changes." },
];

const COMPARISON = [
  { label: "Background", before: "Your default background", after: "The player's own photo" },
  { label: "Layout and text", before: "As you approved it", after: "As you approved it" },
  { label: "Branding", before: "The player's branch theme", after: "The player's branch theme" },
];

const FAQ = [
  { q: "Can I add my own photos?", a: "Yes. Upload a JPG or PNG up to 10 MB." },
  {
    q: "Can players pick a different one for a carousel slide?",
    a: "One background applies to every slide of a post for now.",
  },
  {
    q: "What if a player uploads something off-brand?",
    a: 'Choose "Any library background" or "Suggested only". Your options stay.',
  },
  {
    q: "Why did my design get a photo before I asked for one?",
    a: "We pick a fitting background from the library right away so you're never waiting on image generation. New ones you ask for arrive in the background.",
  },
  {
    q: "Can I change a background with AI?",
    a: "Yes. Refine any background with an instruction, like 'make it warmer' or 'more empty sky at the top'.",
  },
];

export const HowBackgroundsWorkDialog: React.FC<HelpDialogProps> = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={next => onOpenChange(next)} size="xl">
    <Dialog.Header>
      <Dialog.Title className="flex items-center gap-2 text-xl font-bold font-sans">
        <SocoAiLogo variant="mono" height={16} />
        <span>How backgrounds work</span>
      </Dialog.Title>
    </Dialog.Header>

    <Dialog.Content className="max-h-[70vh] overflow-y-auto space-y-6 py-4">
      <p className="text-sm leading-normal text-slate-600">{HOW_BACKGROUNDS_WORK_INTRO}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {EXPLAINERS.map(card => (
          <div
            key={card.title}
            className="bg-slate-50 border border-slate-200/50 p-4 rounded-xl leading-normal text-sm text-slate-600 space-y-2"
          >
            <div className="font-semibold text-slate-800 text-base">{card.title}</div>
            <p>{card.body}</p>
          </div>
        ))}
      </div>

      <figure className="space-y-2">
        <div className="border border-slate-200 rounded-xl overflow-hidden text-sm">
          <div className="grid grid-cols-[140px_1fr_1fr] bg-slate-50 border-b border-slate-200 font-semibold text-slate-700">
            <div className="px-3 py-2">Review card</div>
            <div className="px-3 py-2">Default background</div>
            <div className="px-3 py-2">Player's own photo</div>
          </div>
          {COMPARISON.map(row => (
            <div
              key={row.label}
              className="grid grid-cols-[140px_1fr_1fr] border-b border-slate-200 last:border-b-0 text-slate-600"
            >
              <div className="bg-slate-50 px-3 py-2 font-semibold text-slate-700">{row.label}</div>
              <div className="px-3 py-2">{row.before}</div>
              <div className="px-3 py-2">{row.after}</div>
            </div>
          ))}
        </div>
        <figcaption className="text-xs text-slate-500">Same layout, same text, same branding.</figcaption>
      </figure>

      <div className="flex gap-3 p-4 bg-blue-50/50 border border-blue-100 rounded-xl text-sm text-blue-800 leading-normal">
        <AlertCircle className="size-5 shrink-0 text-blue-500 mt-0.5" aria-hidden="true" />
        <p>Removing a background never affects posts already made with it.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 text-sm leading-normal text-slate-600">
        {FAQ.map(item => (
          <div key={item.q} className="space-y-1">
            <span className="text-base font-bold text-slate-800 flex items-center gap-1">
              <ShieldCheck className="size-4 text-emerald-500 shrink-0" aria-hidden="true" />
              {item.q}
            </span>
            <p>{item.a}</p>
          </div>
        ))}
      </div>
    </Dialog.Content>

    <Dialog.Footer>
      <Button onClick={() => onOpenChange(false)}>Close Help</Button>
    </Dialog.Footer>
  </Dialog>
);
